"use client";

import { cn } from "@ui/lib/utils";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface WordHighlighterProps {
  className?: string;
  displayWords: string[]; // The words to actually display (source of truth)
  endTimes: number[];
  isPlaying: boolean;
  mediaElement: HTMLAudioElement | null;
  onClick?: (wordIndex: number) => void;
  onHighlightedWordChange?: (wordIndex: number) => void;
  renderWord?: (word: string, isHighlighted: boolean, index: number, isPast: boolean) => React.ReactNode;
  startTimes: number[];
  wordAlignment?: number[]; // Optional custom alignment mapping
  words: string[]; // STT words with timing data (may differ from displayText)
}

// Utility function to normalize words for comparison
const normalizeWord = (word: string): string => word.toLowerCase().replace(/\W/g, "");

// Utility function to create alignment mapping between display words and STT words
const createWordAlignment = (displayWords: string[], sttWords: string[]): number[] => {
  // Filter out empty STT words and create a mapping to original indices
  const filteredSTTWords: { originalIndex: number; word: string }[] = [];
  sttWords.forEach((word, index) => {
    const normalized = normalizeWord(word);
    if (normalized.length > 0) {
      filteredSTTWords.push({ word: normalized, originalIndex: index });
    }
  });

  const alignment: number[] = [];
  let sttIndex = 0;

  // First pass: find exact matches with more flexible searching
  for (let displayIndex = 0; displayIndex < displayWords.length; displayIndex++) {
    const displayWord = displayWords[displayIndex];

    // Skip whitespace in display words
    if (/^\s+$/.test(displayWord)) {
      alignment.push(-1); // -1 indicates no timing data (whitespace)
      continue;
    }

    const normalizedDisplayWord = normalizeWord(displayWord);
    if (normalizedDisplayWord.length === 0) {
      alignment.push(-1);
      continue;
    }

    // Find matching STT word - search in a wider window around current position
    let foundMatch = false;
    const searchStart = Math.max(0, sttIndex - 2); // Look back up to 2 positions
    const searchEnd = Math.min(filteredSTTWords.length, sttIndex + 5); // Look ahead up to 5 positions

    for (let i = searchStart; i < searchEnd; i++) {
      if (filteredSTTWords[i].word === normalizedDisplayWord) {
        alignment.push(filteredSTTWords[i].originalIndex);
        sttIndex = i + 1;
        foundMatch = true;
        break;
      }
    }

    // If no exact match found, try partial matching for compound words or contractions
    if (!foundMatch) {
      for (let i = searchStart; i < searchEnd; i++) {
        const sttWord = filteredSTTWords[i].word;
        // Check if either word contains the other (for contractions like "we've" vs "weve")
        if (sttWord.includes(normalizedDisplayWord) || normalizedDisplayWord.includes(sttWord)) {
          alignment.push(filteredSTTWords[i].originalIndex);
          sttIndex = i + 1;
          foundMatch = true;
          break;
        }
      }
    }

    if (!foundMatch) {
      // No matching STT word found, use -1 for now
      alignment.push(-1);
    }
  }

  // Second pass: fill gaps between matched words more conservatively
  for (let i = 0; i < alignment.length; i++) {
    if (alignment[i] === -1 && !/^\s+$/.test(displayWords[i])) {
      // Find the nearest matched words before and after
      let prevMatch = -1;
      let nextMatch = -1;
      let prevDistance = 0;
      let nextDistance = 0;

      // Look backwards for previous match (within reasonable distance)
      for (let j = i - 1; j >= Math.max(0, i - 3); j--) {
        if (alignment[j] !== -1) {
          prevMatch = alignment[j];
          prevDistance = i - j;
          break;
        }
      }

      // Look forwards for next match (within reasonable distance)
      for (let j = i + 1; j <= Math.min(alignment.length - 1, i + 3); j++) {
        if (alignment[j] !== -1) {
          nextMatch = alignment[j];
          nextDistance = j - i;
          break;
        }
      }

      // Only fill gaps if we have strong evidence
      if (prevMatch !== -1 && nextMatch !== -1) {
        // If prev and next are close in STT space, interpolate
        const sttDistance = nextMatch - prevMatch;
        if (sttDistance <= prevDistance + nextDistance + 2) {
          // Use the previous match for words closer to it
          if (prevDistance <= nextDistance) {
            alignment[i] = prevMatch;
          } else {
            alignment[i] = nextMatch;
          }
        }
      }
      // Only extend if very close and confident
      else if (prevMatch !== -1 && prevDistance === 1) {
        alignment[i] = prevMatch;
      } else if (nextMatch !== -1 && nextDistance === 1) {
        alignment[i] = nextMatch;
      }
    }
  }

  // Third pass: ensure monotonic increasing indices
  let lastValidIndex = -1;
  for (let i = 0; i < alignment.length; i++) {
    if (alignment[i] !== -1) {
      if (alignment[i] <= lastValidIndex) {
        // This index is not greater than the previous one, invalidate it
        alignment[i] = -1;
      } else {
        lastValidIndex = alignment[i];
      }
    }
  }

  return alignment;
};

export const WordHighlighter = forwardRef<HTMLDivElement, WordHighlighterProps>(function WordHighlighter(
  {
    mediaElement,
    words,
    startTimes,
    endTimes,
    className,
    onClick,
    displayWords,
    renderWord,
    isPlaying,
    wordAlignment: customWordAlignment,
  },
  ref,
) {
  const [progressWordIndex, setProgressWordIndex] = useState<number>(-1);
  const [highlightedWordIndex, setHighlightedWordIndex] = useState<number>(-1);
  const animationFrameRef = useRef<null | number>(null);
  const lastTimeRef = useRef<number>(0);

  // Create alignment mapping between display words and STT words
  const wordAlignment = useMemo(() => {
    return customWordAlignment || createWordAlignment(displayWords, words);
  }, [customWordAlignment, displayWords, words]);

  // Pre-calculate word time ranges for efficient lookup
  const wordTimeRanges = useMemo(() => {
    return displayWords
      .map((_, displayIndex) => {
        const sttIndex = wordAlignment[displayIndex];
        if (sttIndex === -1) return null;
        return {
          start: startTimes[sttIndex],
          end: endTimes[sttIndex],
          displayIndex,
        };
      })
      .filter(Boolean) as Array<{ displayIndex: number; end: number; start: number }>;
  }, [displayWords, wordAlignment, startTimes, endTimes]);

  // Efficient function to find currently highlighted word index
  const findHighlightedWordIndex = useCallback(
    (currentTime: number): number => {
      // Binary search for the active word range
      let left = 0;
      let right = wordTimeRanges.length - 1;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const range = wordTimeRanges[mid];

        if (currentTime >= range.start && currentTime <= range.end) {
          return range.displayIndex;
        } else if (currentTime < range.start) {
          right = mid - 1;
        } else {
          left = mid + 1;
        }
      }

      // If binary search doesn't find exact match, do linear search for overlapping ranges
      for (const range of wordTimeRanges) {
        if (currentTime >= range.start && currentTime <= range.end) {
          return range.displayIndex;
        }
      }

      return -1;
    },
    [wordTimeRanges],
  );

  // Optimized time update with throttling
  useEffect(() => {
    if (!mediaElement || !isPlaying) return;

    const updateHighlighting = () => {
      const currentTime = mediaElement.currentTime;

      // Only update if time changed significantly (at least 10ms)
      if (Math.abs(currentTime - lastTimeRef.current) < 0.01) {
        animationFrameRef.current = requestAnimationFrame(updateHighlighting);
        return;
      }

      lastTimeRef.current = currentTime;
      const newHighlightedIndex = findHighlightedWordIndex(currentTime);

      // Only update state if the highlighted word actually changed
      setHighlightedWordIndex(prev => {
        if (prev !== newHighlightedIndex) {
          return newHighlightedIndex;
        }
        return prev;
      });

      if (newHighlightedIndex !== -1) {
        setProgressWordIndex(newHighlightedIndex);
      } else {
        // Fallback: find the last word with a timestamp before or equal to current time
        const lastWordIndex = wordTimeRanges
          .filter(range => range.end <= currentTime)
          .sort((a, b) => b.end - a.end)[0]?.displayIndex;

        if (lastWordIndex !== undefined) {
          setProgressWordIndex(lastWordIndex);
        }
      }

      animationFrameRef.current = requestAnimationFrame(updateHighlighting);
    };

    animationFrameRef.current = requestAnimationFrame(updateHighlighting);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mediaElement, findHighlightedWordIndex, isPlaying]);

  const handleClick = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement> | React.MouseEvent<HTMLSpanElement>, wordIndex: number) => {
      if (onClick) {
        e.stopPropagation();
        onClick(wordIndex);
      }
    },
    [onClick],
  );

  // Memoized function to check if a word should be highlighted
  const isHighlighted = useCallback(
    (displayIndex: number) => {
      return highlightedWordIndex === displayIndex;
    },
    [highlightedWordIndex],
  );

  return (
    <div
      className={cn("cursor-text whitespace-pre-wrap", className)}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          handleClick(e, displayWords.length);
        }
      }}
      ref={ref}
      role='button'
      tabIndex={0}
    >
      {displayWords.map((word, index) => {
        const highlighted = isHighlighted(index);
        const isPast = index <= progressWordIndex;

        if (renderWord) {
          return renderWord(word, highlighted, index, isPast);
        }

        return (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
            <span
              className={cn("effect-color -mx-2 cursor-text px-2", {
                "rounded-sm bg-overlay-light": highlighted,
              })}
              key={`word-${index}`}
              onClick={
                renderWord
                  ? undefined
                  : e => {
                      handleClick(e, index);
                    }
              }
              role={renderWord ? undefined : "button"}
            >
              {word}
            </span>
          </>
        );
      })}
    </div>
  );
});
